/**
 * WebSocket client that connects to the backend proxy
 */

import { EventEmitter } from "eventemitter3";
import { LiveClientEventTypes } from "./genai-live-client";
import {
  LiveConnectConfig,
  LiveServerMessage,
  Part,
  LiveClientToolResponse,
} from "@google/genai";
import { StreamingLog } from "../types";
import { base64ToArrayBuffer } from "./utils";

export class BackendProxyClient extends EventEmitter<LiveClientEventTypes> {
  private ws: WebSocket | null = null;
  private _status: "connected" | "disconnected" | "connecting" = "disconnected";
  private backendUrl: string;
  private _model: string | null = null;
  protected config: LiveConnectConfig | null = null;

  public get status() {
    return this._status;
  }

  public get model() {
    return this._model;
  }

  public get session() {
    return this.ws;
  }

  public getConfig() {
    return { ...this.config };
  }

  constructor(backendUrl: string) {
    super();
    this.backendUrl = backendUrl;
    this.send = this.send.bind(this);
    this.onopen = this.onopen.bind(this);
    this.onerror = this.onerror.bind(this);
    this.onclose = this.onclose.bind(this);
    this.onmessage = this.onmessage.bind(this);
  }

  protected log(type: string, message: StreamingLog["message"]) {
    const log: StreamingLog = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  async connect(model: string, config: LiveConnectConfig): Promise<boolean> {
    if (this._status === "connected" || this._status === "connecting") {
      return false;
    }

    this._status = "connecting";
    this.config = config;
    this._model = model;

    return new Promise<boolean>((resolve, reject) => {
      try {
        const wsUrl = this.backendUrl.replace(/^http/, "ws");
        const url = new URL("/ws", wsUrl);
        url.searchParams.set("model", model);
        url.searchParams.set("config", encodeURIComponent(JSON.stringify(config)));

        this.ws = new WebSocket(url.toString());

        this.ws.onopen = () => {
          this.onopen();
          resolve(true);
        };
        this.ws.onmessage = this.onmessage;
        this.ws.onerror = (err) => {
          this.onerror(err);
          reject(err);
        };
        this.ws.onclose = this.onclose;

      } catch (e) {
        console.error("Error connecting to backend proxy:", e);
        this._status = "disconnected";
        reject(e);
      }
    });
  }

  public disconnect() {
    if (!this.ws) {
      return false;
    }
    this.ws.close();
    this.ws = null;
    this._status = "disconnected";
    this.log("client.close", `Disconnected`);
    return true;
  }

  protected onopen() {
    this._status = "connected";
    this.log("client.open", "Connected to backend proxy");
    this.emit("open");
  }

  protected onerror(e: Event) {
    this.log("server.error", "WebSocket error");
    this.emit("error", e as ErrorEvent);
  }

  protected onclose(e: CloseEvent) {
    this._status = "disconnected";
    this.log(
      `server.close`,
      `disconnected ${e.reason ? `with reason: ${e.reason}` : ``}`
    );
    this.emit("close", e);
  }

  protected async onmessage(event: MessageEvent) {
    try {
      const data = JSON.parse(event.data);

      if (data.type === "connected") {
        // Backend successfully connected to Vertex AI
        return;
      }

      if (data.type === "error") {
        this.log("server.error", data.error);
        return;
      }

      if (data.type === "closed") {
        this.log("server.close", data.reason || "Connection closed");
        return;
      }

      if (data.type === "vertex_message") {
        // Forward Vertex AI message to the app
        const message: LiveServerMessage = data.data;
        await this.handleVertexMessage(message);
      }
    } catch (e) {
      console.error("Error parsing message from backend:", e);
    }
  }

  protected async handleVertexMessage(message: LiveServerMessage) {
    if (message.setupComplete) {
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }

    if (message.toolCall) {
      this.log("server.toolCall", message);
      this.emit("toolcall", message.toolCall);
      return;
    }

    if (message.toolCallCancellation) {
      this.log("server.toolCallCancellation", message);
      this.emit("toolcallcancellation", message.toolCallCancellation);
      return;
    }

    if (message.serverContent) {
      const { serverContent } = message;
      if ("interrupted" in serverContent) {
        this.log("server.content", "interrupted");
        this.emit("interrupted");
        return;
      }
      if ("turnComplete" in serverContent) {
        this.log("server.content", "turnComplete");
        this.emit("turncomplete");
      }

      if ("modelTurn" in serverContent) {
        let parts: Part[] = serverContent.modelTurn?.parts || [];

        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType?.startsWith("audio/pcm")
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        const otherParts = parts.filter((p) => !audioParts.includes(p));

        base64s.forEach((b64) => {
          if (b64) {
            const data = base64ToArrayBuffer(b64);
            this.emit("audio", data);
            this.log(`server.audio`, `buffer (${data.byteLength})`);
          }
        });

        if (!otherParts.length) {
          return;
        }

        parts = otherParts;
        const content: any = { modelTurn: { parts } };
        this.emit("content", content);
        this.log(`server.content`, message);
      }
    }
  }

  sendRealtimeInput(chunks: Array<{ mimeType: string; data: string }>) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    for (const ch of chunks) {
      this.ws.send(
        JSON.stringify({
          type: "send",
          method: "sendRealtimeInput",
          data: { media: ch },
        })
      );
    }

    let hasAudio = false;
    let hasVideo = false;
    for (const ch of chunks) {
      if (ch.mimeType.includes("audio")) hasAudio = true;
      if (ch.mimeType.includes("image")) hasVideo = true;
    }
    const message =
      hasAudio && hasVideo
        ? "audio + video"
        : hasAudio
        ? "audio"
        : hasVideo
        ? "video"
        : "unknown";
    this.log(`client.realtimeInput`, message);
  }

  sendToolResponse(toolResponse: LiveClientToolResponse) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    if (
      toolResponse.functionResponses &&
      toolResponse.functionResponses.length
    ) {
      this.ws.send(
        JSON.stringify({
          type: "send",
          method: "sendToolResponse",
          data: toolResponse,
        })
      );
      this.log(`client.toolResponse`, toolResponse);
    }
  }

  send(parts: Part | Part[], turnComplete: boolean = true) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    this.ws.send(
      JSON.stringify({
        type: "send",
        method: "sendClientContent",
        data: { turns: Array.isArray(parts) ? parts : [parts], turnComplete },
      })
    );
    this.log(`client.send`, {
      turns: Array.isArray(parts) ? parts : [parts],
      turnComplete,
    });
  }
}
